import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, debounceTime, map, of, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ISidebarLink } from '../navigation/sidebar/sidebar.component';
import { StatusComponent } from '../status/status.component';
import { MessageService } from '../../../services/message/message.service';
import { SettingsService } from '../../../services/settings/settings.service';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { UserProfileService } from '../../../services/user/user-profile.service';
import { KeycloakAdminService } from '../../../services/keycloak/keycloak.service';
import { TBlkCxpRole } from '../../../types/settingsDataModule';
import { TPermissionCategory, TPermissionKey } from '../../../../utils/rolesDataModule';
import { formatWhen, sendMoment } from '../../../../utils/sms';
import { BackdropDismiss, CommandPaletteService, trapTab } from './command-palette.service';

type ItemKind = 'action' | 'page' | 'message';
type SearchState = 'idle' | 'loading' | 'ready' | 'error';

interface PaletteItem {
  key: string;
  kind: ItemKind;
  title: string;
  /** Right-hand context: the channel for a page, a short line for an action. */
  hint: string;
  icon: string;
  /** Extra words the filter matches on. */
  terms: string;
  run: () => void;
  statusText?: string;
  status?: string;
}

interface IndexedItem extends PaletteItem {
  index: number;
}

interface PaletteGroup {
  id: string;
  label: string;
  items: IndexedItem[];
}

/** Fewer letters than this only filter pages and actions. */
const MIN_MESSAGE_QUERY = 2;
const MAX_MESSAGES = 6;

/**
 * ⌘K / Ctrl+K palette. Goes to any page the person can see (the shell's
 * sidebar links, so the API traffic role gate carries over), runs a few
 * quick actions, and finds messages by subject. It opens and closes
 * without animation because it is used many times a day. The shell
 * creates it on open, so every open starts empty.
 */
@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [StatusComponent],
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.css',
})
export class CommandPaletteComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private state = inject(CommandPaletteService);
  private messageService = inject(MessageService);
  private settingsService = inject(SettingsService);
  private permissions = inject(PermissionsService);
  private userProfile = inject(UserProfileService);
  private keycloak = inject(KeycloakAdminService);

  @ViewChild('dialog') dialogRef?: ElementRef<HTMLElement>;
  @ViewChild('input') inputRef?: ElementRef<HTMLInputElement>;

  @Input() set links(value: ISidebarLink[] | null | undefined) {
    this.linkList.set(value ?? []);
  }

  readonly listId = 'bk-cmdk-list';

  readonly query = signal('');
  readonly activeIndex = signal(0);
  readonly searchState = signal<SearchState>('idle');
  private readonly messages = signal<any[]>([]);
  private readonly linkList = signal<ISidebarLink[]>([]);
  private readonly roles = signal<TBlkCxpRole[]>([]);

  private readonly search$ = new Subject<string>();
  private readonly cache = new Map<string, any[]>();
  private returnFocus: HTMLElement | null = null;
  private pointer = { x: -1, y: -1 };
  readonly backdrop = new BackdropDismiss();

  readonly trimmed = computed(() => this.query().trim());

  private readonly pages = computed<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];
    const add = (link: ISidebarLink, group: string) => {
      if (!link.link) return;
      items.push({
        key: `page:${link.link}`,
        kind: 'page',
        title: link.title,
        hint: group,
        icon: link.iconName,
        terms: `${group} page go open`,
        run: () => this.router.navigateByUrl(link.link!),
      });
    };

    for (const item of this.linkList()) {
      if (item.children?.length) item.children.forEach((child) => add(child, item.title));
      else add(item, '');
    }

    // Settings sits in the sidebar footer; its two open tabs are listed here.
    add({ iconName: 'person', title: 'Profile', link: '/organization/settings/profile' }, 'Settings');
    add(
      { iconName: 'apartment', title: 'Organization settings', link: '/organization/settings/organization' },
      'Settings'
    );
    return items;
  });

  private readonly actions = computed<PaletteItem[]>(() => {
    const roles = this.roles();
    const can = (category: TPermissionCategory, key: TPermissionKey) =>
      this.permissions.checkPermission(roles, category, key);
    const items: PaletteItem[] = [];

    if (can('MESSAGE_CONTENT_MANAGEMENT', 'compose-sms')) {
      items.push({
        key: 'action:compose',
        kind: 'action',
        title: 'Compose message',
        hint: 'New SMS',
        icon: 'edit_square',
        terms: 'new sms send write message',
        run: () => this.router.navigate(['/organization/messages/create']),
      });
    }

    if (can('CAMPAIGN_MANAGEMENT', 'create-campaigns')) {
      items.push({
        key: 'action:campaign',
        kind: 'action',
        title: 'New campaign',
        hint: 'Campaigns',
        icon: 'campaign',
        terms: 'create campaign',
        run: () => this.router.navigate(['/organization/campaigns/create-campaign']),
      });
    }

    if (can('CONTACT_MANAGEMENT', 'create-contacts')) {
      items.push({
        key: 'action:contacts',
        kind: 'action',
        title: 'Add contacts',
        hint: 'Type a few in rows',
        icon: 'group_add',
        terms: 'new contact people create',
        run: () => this.router.navigate(['/organization/contacts/multiple-contacts']),
      });

      if (can('CONTACT_MANAGEMENT', 'import-contacts-csv-excel')) {
        items.push({
          key: 'action:import',
          kind: 'action',
          title: 'Import contacts',
          hint: 'CSV or Excel file',
          icon: 'upload_file',
          terms: 'add contacts upload spreadsheet csv excel',
          run: () => this.router.navigate(['/organization/contacts/csv']),
        });
      }
    }

    items.push({
      key: 'action:shortcuts',
      kind: 'action',
      title: 'Show keyboard shortcuts',
      hint: '?',
      icon: 'keyboard',
      terms: 'help keys hotkeys',
      // Hand over where focus was before the palette, since its input goes away.
      run: () => this.state.openShortcuts(this.returnFocus),
    });

    return items;
  });

  private readonly messageItems = computed<PaletteItem[]>(() =>
    this.messages().map((message: any, i) => {
      const archived = message?.archive === true;
      const status = String(message?.status ?? '');
      const when = formatWhen(sendMoment(message));
      return {
        key: `message:${message?.uuid ?? message?.id ?? i}`,
        kind: 'message' as const,
        title: String(message?.messageSubject ?? '').trim() || 'Untitled message',
        hint: when === '—' ? '' : when,
        icon: 'chat_bubble',
        terms: '',
        statusText: archived ? 'Archived' : status,
        status: archived ? 'archived' : status,
        run: () => this.openMessage(message),
      };
    })
  );

  readonly groups = computed<PaletteGroup[]>(() => {
    const words = this.trimmed().toLowerCase().split(/\s+/).filter(Boolean);
    const groups: { id: string; label: string; items: PaletteItem[] }[] = [
      { id: 'actions', label: 'Actions', items: this.filter(this.actions(), words) },
      { id: 'pages', label: 'Pages', items: this.filter(this.pages(), words) },
    ];
    if (words.length && this.trimmed().length >= MIN_MESSAGE_QUERY) {
      groups.push({ id: 'messages', label: 'Messages', items: this.messageItems() });
    }

    let index = 0;
    return groups
      .filter((group) => group.items.length)
      .map((group) => ({
        ...group,
        items: group.items.map((item) => ({ ...item, index: index++ })),
      }));
  });

  readonly flat = computed<IndexedItem[]>(() => this.groups().flatMap((group) => group.items));

  readonly activeId = computed(() => {
    const item = this.flat()[this.activeIndex()];
    return item ? this.optionId(item.index) : null;
  });

  /** One line under the list: search progress, misses and errors. */
  readonly statusLine = computed(() => {
    const q = this.trimmed();
    if (!q) return '';
    const state = this.searchState();
    const nothingElse = !this.flat().length;

    if (q.length < MIN_MESSAGE_QUERY) {
      return nothingElse
        ? `Nothing matches “${q}”. Type another letter to search message subjects too.`
        : 'Type another letter to search message subjects too.';
    }
    if (state === 'loading') return 'Searching message subjects…';
    if (state === 'error') return 'Messages could not be searched. Check your connection, then type again.';
    if (state === 'ready' && !this.messages().length) {
      return nothingElse
        ? `Nothing matches “${q}”. Try a page name or another word from the subject.`
        : `No message subjects contain “${q}”.`;
    }
    return '';
  });

  /** What a screen reader hears as results change. */
  readonly announcement = computed(() => {
    const count = this.flat().length;
    const line = this.statusLine();
    if (!this.trimmed()) return '';
    const results = count === 1 ? '1 result' : `${count} results`;
    return line ? `${results}. ${line}` : results;
  });

  constructor() {
    if (typeof document !== 'undefined') {
      this.returnFocus = document.activeElement as HTMLElement | null;
    }
  }

  ngOnInit() {
    this.settingsService.getUserRoles
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((roles) => this.roles.set(roles ?? []));

    this.search$
      .pipe(
        debounceTime(250),
        switchMap((q) => {
          if (q.length < MIN_MESSAGE_QUERY) return of(null);
          const cached = this.cache.get(q.toLowerCase());
          if (cached) return of({ q, items: cached, failed: false });
          return this.searchMessages(q).pipe(
            map((items) => ({ q, items, failed: false })),
            catchError(() => of({ q, items: [] as any[], failed: true }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (!result || result.q !== this.trimmed()) return;
        if (result.failed) {
          this.searchState.set('error');
          this.messages.set([]);
          return;
        }
        this.cache.set(result.q.toLowerCase(), result.items);
        this.messages.set(result.items);
        this.searchState.set('ready');
      });
  }

  ngAfterViewInit() {
    this.inputRef?.nativeElement.focus();
  }

  ngOnDestroy() {
    const target = this.returnFocus;
    if (target && typeof document !== 'undefined' && document.contains(target)) {
      // After the dialog leaves the page, so focus is not pulled back into it.
      setTimeout(() => {
        if (document.activeElement === document.body || !document.activeElement) target.focus();
      });
    }
  }

  optionId(index: number) {
    return `bk-cmdk-opt-${index}`;
  }

  onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);
    this.activeIndex.set(0);

    const q = value.trim();
    if (q.length >= MIN_MESSAGE_QUERY) {
      const cached = this.cache.get(q.toLowerCase());
      if (cached) {
        this.messages.set(cached);
        this.searchState.set('ready');
      } else {
        this.messages.set([]);
        this.searchState.set('loading');
      }
    } else {
      this.messages.set([]);
      this.searchState.set('idle');
    }
    this.search$.next(q);
    this.scrollActiveIntoView();
  }

  onDialogKeydown(event: Event) {
    const key = event as KeyboardEvent;
    if (key.isComposing) return;

    switch (key.key) {
      case 'Escape':
        key.preventDefault();
        key.stopPropagation();
        this.close();
        return;
      case 'Tab':
        trapTab(key, this.dialogRef?.nativeElement);
        return;
    }

    if (key.target !== this.inputRef?.nativeElement) return;
    const count = this.flat().length;

    switch (key.key) {
      case 'ArrowDown':
        key.preventDefault();
        if (count) this.activeIndex.set((this.activeIndex() + 1) % count);
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        key.preventDefault();
        if (count) this.activeIndex.set((this.activeIndex() - 1 + count) % count);
        this.scrollActiveIntoView();
        break;
      case 'PageDown':
        key.preventDefault();
        if (count) this.activeIndex.set(Math.min(count - 1, this.activeIndex() + 5));
        this.scrollActiveIntoView();
        break;
      case 'PageUp':
        key.preventDefault();
        if (count) this.activeIndex.set(Math.max(0, this.activeIndex() - 5));
        this.scrollActiveIntoView();
        break;
      case 'Enter': {
        key.preventDefault();
        const item = this.flat()[this.activeIndex()];
        if (item) this.run(item);
        break;
      }
    }
  }

  /**
   * Real pointer movement only: browsers send a mousemove when the list
   * scrolls under a still pointer, which would steal the keyboard selection.
   */
  hover(index: number, event: MouseEvent) {
    if (event.clientX === this.pointer.x && event.clientY === this.pointer.y) return;
    this.pointer = { x: event.clientX, y: event.clientY };
    if (this.activeIndex() !== index) this.activeIndex.set(index);
  }

  onBackdropClick(event: Event) {
    if (this.backdrop.shouldClose(event)) this.close();
  }

  run(item: PaletteItem) {
    this.close();
    item.run();
  }

  close() {
    this.state.closePalette();
  }

  private openMessage(message: any) {
    const id = message?.uuid ?? message?.id;
    if (!id) return;
    // Hand the row over so the details page can show it while it loads.
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages', id]);
  }

  private filter(items: PaletteItem[], words: string[]): PaletteItem[] {
    if (!words.length) return items;
    const phrase = words.join(' ');
    return items
      .map((item, order) => {
        const title = item.title.toLowerCase();
        const haystack = `${title} ${item.hint} ${item.terms}`.toLowerCase();
        if (!words.every((word) => haystack.includes(word))) return null;
        const rank = title.startsWith(phrase)
          ? 0
          : title.split(/\s+/).some((part) => part.startsWith(words[0]))
          ? 1
          : title.includes(words[0])
          ? 2
          : 3;
        return { item, rank, order };
      })
      .filter((entry): entry is { item: PaletteItem; rank: number; order: number } => !!entry)
      .sort((a, b) => a.rank - b.rank || a.order - b.order)
      .map((entry) => entry.item);
  }

  /**
   * The Messages list endpoint, filtered by subject. Read-only and kept off
   * MessageService's shared lists, so an open Messages page is unaffected.
   */
  private searchMessages(subject: string): Observable<any[]> {
    const organizationId = this.userProfile.organizationId;
    if (!organizationId) return of([]);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.keycloak.getKcAccessToken()}`,
      'X-Organisation-ID': organizationId,
    });

    return this.http
      .get<any>(
        `${environment.BASE_URL}/blackkonnectcxpengagement/api/v1/organization/${organizationId}/engagement-messages`,
        {
          headers,
          params: {
            'messageSubject.contains': subject,
            sort: 'createdAt,desc',
            page: 0,
            size: MAX_MESSAGES,
          },
        }
      )
      .pipe(
        map((data) => {
          const items = Array.isArray(data?.items) ? data.items : [];
          return items.filter((item: any) => !!item && typeof item === 'object').slice(0, MAX_MESSAGES);
        })
      );
  }

  private scrollActiveIntoView() {
    if (typeof document === 'undefined') return;
    // After the list re-renders with the new selection.
    setTimeout(() => {
      const id = this.activeId();
      if (!id || this.activeIndex() === 0) {
        // The first result: show its group label too.
        this.dialogRef?.nativeElement.querySelector('.cmdk__list')?.scrollTo({ top: 0 });
        return;
      }
      document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
    });
  }
}
